/**
 * MyBib — free citation / bibliography generator
 *
 * Declares a single web-accessible-resource entry — `popup.htm` and `static/*` —
 * matched to every origin with no `use_dynamic_url`. That glob exposes the whole
 * `static/` tree (images and CSS) at a fixed URL reachable from any page.
 *
 * The logo image is the cleanest signal: `static/images/logo-128.png` is vendor-named
 * and, being an image, loads through `probeImage` with no CORS grant, so it survives a
 * restrictive page CSP.
 *
 * Manifest verified against version 1.2.8.
 */

import { probeImage, probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'phidhnmbkbkbkbknhldmpmnacgicphkf';

export const myBib: ExtensionDefinition = {
  id: ID,
  name: 'MyBib: Free Citation Generator',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 1_000_000,
  notes:
    'Exposes popup.htm and the whole static/* tree to every origin with no dynamic URL. ' +
    'The vendor-named logo image is conclusive and loads without a CORS grant.',
  signals: [
    {
      id: 'war-logo-128-image',
      kind: 'web-accessible-resource',
      description:
        'Load static/images/logo-128.png via <img> — a vendor-named icon exposed to ' +
        'every origin; needs no CORS grant, so it works even under a strict page CSP.',
      weight: 1,
      run: () => probeImage(ID, 'static/images/logo-128.png'),
    },
    {
      id: 'war-logo-32-image',
      kind: 'web-accessible-resource',
      description:
        'Load static/images/logo-32.png via <img> — a second icon size, corroborating ' +
        'the identification and surviving either one being renamed.',
      weight: 0.5,
      run: () => probeImage(ID, 'static/images/logo-32.png'),
    },
    {
      id: 'war-tachyons-css',
      kind: 'web-accessible-resource',
      description:
        'Fetch static/css/tachyons.min.css — a stylesheet under the same static/ tree, ' +
        'an independent path from the images.',
      weight: 0.5,
      run: () => probeFetch(ID, 'static/css/tachyons.min.css'),
    },
  ],
};
